<script lang="ts" setup>
import type { FavoriteList } from '~/models/Favorites'
import type { Task } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faEllipsis, faFolderOpen, faLink } from '@fortawesome/duotone-regular-svg-icons'
import { colors, forceColorShade, getColorForId } from '~/utils/colors'
import { AuToast } from '~/stores/toasts'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useFileInfo from '~/composables/useFileInfo'
import { useFavorites } from '~/composables/useFavorites'
import Button from '~/components/ui/Button.vue'
import FavoriteDialog from '~/components/favorites/dialogs/FavoriteDialog.vue'
import ShareContentDialog from '~/components/dialogs/ShareContentDialog.vue'
import StackedCard from '~/components/cards/StackedCard.vue'
import CopyUrlButton from '~/components/cards/buttons/CopyUrlButton.vue'
import CardMenu from '~/components/cards/buttons/CardMenu.vue'
import SharedCardBadge from '~/components/cards/badges/SharedCardBadge.vue'

const { list, task } = defineProps<{ list: FavoriteList, task?: Task }>()

const { t } = useI18n()
const { isTeacher } = storeToRefs(useAuthStore())
const { routeToAbsoluteUrl } = useUrlBuilder()
const {
  deleteFavoriteList,
  updateFavoriteList,
  duplicateFavoriteList,
  canDeleteList,
  canEditList,
  getColor,
  safeName,
} = useFavorites()
const { readableMimeType, getFileIcon } = useFileInfo()

const color = computed(() => getColorForId(list.id ?? 0, colors))
const favoritesWithImages = computed(() => list.favorites
  ?.filter(favorite => !!favorite.aunivers?.imageUrl || !!favorite.file || !!favorite.link) ?? [])
const listRoute = computed(() => ({ name: 'favorite_list', params: { listId: list.id } }))
const listUrl = computed(() => routeToAbsoluteUrl(listRoute.value))
const iconColor = computed(() => forceColorShade(getColor(list), '50'))
</script>

<template>
  <StackedCard
    :color="forceColorShade(color, '5')"
    :item="list"
    hover-effect
  >
    <template #default="{ item }">
      <div
        :style="{ border: `1px solid ${forceColorShade(color, '20')}` }"
        class="relative space-y-4 rounded-md p-4 transition-all duration-150 has-focus-visible:ring"
      >
        <SharedCardBadge v-if="task?.homework" type="list" />
        <div
          v-if="favoritesWithImages.length"
          :class="{
            'grid-cols-1 grid-rows-1': favoritesWithImages.length === 1,
            'grid-cols-2 grid-rows-1': favoritesWithImages.length === 2,
            'grid-cols-2 grid-rows-2': favoritesWithImages.length > 2,
          }"
          class="grid aspect-[6/3] w-full gap-px overflow-hidden rounded"
        >
          <template v-for="(favorite, i) in favoritesWithImages.slice(0, 4)">
            <img
              v-if="favorite.aunivers?.imageUrl"
              :key="`thumb-item-${i}`"
              :src="favorite.aunivers.imageUrl"
              :alt="favorite.aunivers.imageAltText ?? ''"
              class="size-full object-cover"
            >
            <div
              v-else-if="favorite.file?.mimeType"
              :key="`thumb-file-${i}`"
              class="flex h-full w-full items-center justify-center"
              :style="{ color: iconColor }"
              :title="readableMimeType(favorite.file.mimeType)"
            >
              <font-awesome-icon
                :icon="getFileIcon(favorite.file.mimeType)"
                size="2x"
              />
            </div>
            <div
              v-else-if="favorite.link?.url"
              :key="`thumb-link-${i}`"
              class="flex h-full w-full items-center justify-center"
              :style="{ color: iconColor }"
              :title="t('favorite.externalLink')"
            >
              <font-awesome-icon
                :icon="faLink"
                size="2x"
              />
            </div>
          </template>
        </div>
        <div
          v-else
          :style="{ background: forceColorShade(color, '10') }"
          class="flex aspect-[6/3] items-center justify-center rounded"
        >
          <font-awesome-icon
            :icon="faFolderOpen"
            :style="{ color: forceColorShade(color, '50') }"
            size="3x"
          />
        </div>
        <div>
          <h3 class="text-xl font-bold">
            <router-link
              :data-pendo="PendoFeature.Favorites.OpenList"
              class="line-clamp-3 before:absolute before:inset-0 before:rounded-md before:outline-0"
              :to="listRoute"
            >
              {{ safeName(item?.name || '') }}
            </router-link>
          </h3>
          <p
            class="text-base"
            v-text="t('favorite.counted', { number: Number(item?.favorites?.length ?? 0) }, Number(item?.favorites?.length ?? 0))"
          />
        </div>
        <div class="flex items-center justify-between">
          <CardMenu
            v-if="item"
            :favorite-list="item"
          >
            <template #toggle>
              <Button
                :color
                shade="dark"
                shape="square"
                size="lg"
                variant="ghost"
                class="z-10"
              >
                <font-awesome-icon :icon="faEllipsis" />
              </Button>
            </template>
            <template #menu>
              <FavoriteDialog
                :disabled="!canEditList(list)"
                :item="item"
                action="edit"
                @update="(favoriteList) => updateFavoriteList.mutateAsync({ list: favoriteList })
                  .then(() => AuToast.success(t('favorite.updateSuccess')))
                  .catch(() => AuToast.error(t('error')))"
              />
              <FavoriteDialog
                :disabled="!isTeacher"
                :item="item"
                action="duplicate"
                @duplicate="(favoriteList) => duplicateFavoriteList.mutateAsync(favoriteList)
                  .then(() => AuToast.success(t('favorite.duplicateSuccess')))
                  .catch(() => AuToast.error(t('error')))"
              />
              <CopyUrlButton
                v-if="item"
                :url="listUrl"
              />
              <FavoriteDialog
                :disabled="!canDeleteList(list)"
                :item="item"
                action="delete"
                @delete="(favoriteList) => deleteFavoriteList.mutateAsync(favoriteList)
                  .then(() => AuToast.success(t('favorite.deleteSuccess')))
                  .catch(() => AuToast.error(t('error')))"
              />
            </template>
          </CardMenu>
          <ShareContentDialog
            v-if="isTeacher"
            :color
            :list
            show-label
            size="lg"
            class="z-10"
          />
        </div>
      </div>
    </template>
  </StackedCard>
</template>
